import { InstitutionalTrustSection } from '@/components/brand/institutional-trust-section'
import { CoreMoatSection } from '@/components/home/core-moat-section'
import { FeatureSection } from '@/components/home/feature-section'
import { HeroSection } from '@/components/home/hero-section'
import { LearnMoreSection } from '@/components/home/learn-more-section'
import { PublishedCompetitionsSection } from '@/components/home/published-competitions-section'
import { RoleCards } from '@/components/home/role-cards'
import { useSession } from '@/features/session/use-session'

/**
 * What every visitor sees at `/` — a short welcome, not diagnostics.
 *
 * PR-053 (v3.1.2, TASK-U-009): this page used to render only for an
 * anonymous visitor (`RootRedirectPage` sent anyone signed in elsewhere
 * before this ever mounted). `/` is now auth-independent — the "首頁"
 * nav item must show the same content whether or not the visitor is signed
 * in, rather than turning into a personalized Dashboard (that content now
 * lives at the separate "總覽" nav item, see `home-page.tsx`).
 *
 * The one exception is `HeroSection`'s primary "立即登入" CTA, which
 * pre-dates this task (UX-023/AC-042): it must never be the primary action
 * shown to an already-authenticated session, so this component still reads
 * `useSession()` for that one purpose. Everything else renders identically
 * either way.
 *
 * Sign-in itself lives entirely at `/login` (email, organization, and — in
 * development — the dev sign-in tool): the hero's primary CTA and every role
 * card already point there, so this page no longer duplicates the forms. A
 * role card clicked while already signed in still resolves correctly —
 * `/login` sends an authenticated visitor straight to `/app`.
 *
 * `PublishedCompetitionsSection` sits right after the hero, before the role
 * cards — real, currently public competitions are the first proof a visitor
 * gets that the platform is actually running something, ahead of the pitch.
 *
 * `InstitutionalTrustSection` (PR-117) is deliberately last, after every
 * product/feature section and before the layout's global `SiteFooter` — the
 * product pitch comes first, and "who backs and builds this" (sponsoring
 * unit, partner universities, NAPAI program leadership) is the credibility
 * section a government/university project site answers only once, not
 * something to lead with above the hero.
 */
export function AnonymousLandingPage() {
  const session = useSession()

  if (session.isPending) {
    return null
  }

  const authenticated = session.data?.authenticated === true

  return (
    <div className="flex flex-col gap-6">
      <HeroSection authenticated={authenticated} />
      <PublishedCompetitionsSection />
      <RoleCards />
      <FeatureSection />
      <CoreMoatSection />
      <LearnMoreSection />
      <InstitutionalTrustSection />
    </div>
  )
}
